import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AppIcon } from '@components/icon/icon.component';
import { Reservation, ReservationStatus, Table } from '@model/reservation';
import { ReservationTag, ReservationTagCategory } from '@model/reservation-tag';
import { Section } from '@model/tables-plan';
import { Store, select } from '@ngrx/store';
import * as ReservationListActions from '@store/actions/reservation-list.actions';
import * as ReservationActions from '@store/actions/reservation.actions';
import { TablesPlanActions } from '@store/actions/tables-plan.actions';
import { State } from '@store/index';
import * as AppSelectors from '@store/selectors/app.selectors';
import * as ReservationSelectors from '@store/selectors/reservation.selectors';
import * as TablesPlanSelectors from '@store/selectors/tables-plan.selectors';
import moment from 'moment';
import { Observable, Subject, takeUntil } from 'rxjs';
import { Common } from 'src/app/lib/common-methods';
import { ReservationService } from 'src/app/lib/reservation.service';
import { UtilsService } from 'src/app/lib/utils.service';

@Component({
  selector: 'app-tables-plan-use-reservation',
  templateUrl: './tables-plan-use-reservation.component.html',
  styleUrl: './tables-plan-use-reservation.component.scss'
})
export class TablesPlanUseReservationComponent implements OnInit, OnDestroy {

  constructor(
    private store: Store<State>,
    private reservationService: ReservationService,
    private router: Router,
    private utilsService: UtilsService,
  ) {

  }

  destroy$: Subject<boolean> = new Subject<boolean>();

  @Input() reservation: Reservation | null = null;

  reservationAssignTablesMode$: Observable<boolean> = this.store.pipe(select(TablesPlanSelectors.selectReservationAssignTablesMode));
  reservationAssignTablesMode: boolean = false;
  assignTablesReservation$: Observable<Reservation | null> = this.store.pipe(select(TablesPlanSelectors.selectAssignTablesReservation));
  assignTablesReservation: Reservation | null = null;
  tags$: Observable<ReservationTagCategory[] | null> = this.store.pipe(select(AppSelectors.selectTags));
  tags: ReservationTagCategory[] = [];
  restaurantHasOnlineCategories$: Observable<boolean> = this.store.pipe(select(AppSelectors.selectRestaurantHasOnlineCategories));
  restaurantHasOnlineCategories: boolean = false;
  tables$: Observable<Table[]> = this.store.pipe(select(TablesPlanSelectors.selectTables));
  tables: Table[] = [];
  selectedSection$: Observable<Section | null | undefined> = this.store.pipe(select(TablesPlanSelectors.selectSelectedSection));
  selectedSection: Section | null | undefined = null;
  sections$: Observable<Section[] | null> = this.store.pipe(select(TablesPlanSelectors.selectSections));
  sections: Section[] | null = null;
  quickChangingStatus$: Observable<boolean> = this.store.pipe(select(ReservationSelectors.selectQuickChangingStatus));
  quickChangingStatus: boolean = false;
  restaurantHasExtraInfo$: Observable<boolean> = this.store.pipe(select(AppSelectors.selectRestaurantHasExtraInfo));
  restaurantHasExtraInfo: boolean = false;

  allergies: string[] = [];
  dietaryTags: ReservationTag[] = [];

  get hasDiscount() {
    if (this.reservation?.reservationDetails?.discount == null) {
      return false;
    }

    const isNotNumber = isNaN(this.reservation.reservationDetails.discount as number);

    if (isNotNumber) {
      return false;
    }

    const discount = parseFloat(`${this.reservation.reservationDetails.discount}`);

    return discount > 0;
  }

  get hasSource() {
    return this.reservation?.sourceDetails?.sourceType != null && this.reservation.sourceDetails?.sourceType.length > 0
  }

  ngOnInit() {

    const reservationAllergies = (this.reservation?.reservationDetails?.allergy ?? '').split(',').filter(a => !Common.isNullOrEmpty(a));
    const customerAllergies = (this.reservation?.customerDetails?.allergy ?? '').split(',').filter(a => !Common.isNullOrEmpty(a));
    this.allergies = reservationAllergies.concat(customerAllergies);

    this.reservationAssignTablesMode$.pipe(takeUntil(this.destroy$)).subscribe((reservationAssignTablesMode: boolean) => {
      this.reservationAssignTablesMode = reservationAssignTablesMode;
    });

    this.assignTablesReservation$.pipe(takeUntil(this.destroy$)).subscribe((assignTablesReservation: Reservation | null) => {
      this.assignTablesReservation = assignTablesReservation;
    });

    this.tags$.pipe(takeUntil(this.destroy$)).subscribe((tags) => {
      this.tags = tags ?? [];
    });

    this.restaurantHasOnlineCategories$.pipe(takeUntil(this.destroy$)).subscribe((restaurantHasOnlineCategories: boolean) => {
      this.restaurantHasOnlineCategories = restaurantHasOnlineCategories;
    });

    this.tables$.pipe(takeUntil(this.destroy$)).subscribe((tables: Table[]) => {
      this.tables = tables;
    });

    this.selectedSection$.pipe(takeUntil(this.destroy$)).subscribe((selectedSection: Section | null | undefined) => {
      this.selectedSection = selectedSection;
    });

    this.sections$.pipe(takeUntil(this.destroy$)).subscribe((sections) => {
      this.sections = sections;
    });
    
    this.quickChangingStatus$.pipe(takeUntil(this.destroy$)).subscribe((quickChangingStatus: boolean) => {
      
      if (this.quickChangingStatus === true && quickChangingStatus === false) {
        this.deselctTables();
      }
      
      this.quickChangingStatus = quickChangingStatus;
    });

    this.restaurantHasExtraInfo$.pipe(takeUntil(this.destroy$)).subscribe((restaurantHasExtraInfo) => {
      this.restaurantHasExtraInfo = restaurantHasExtraInfo;
    });

    this.tags$.pipe(takeUntil(this.destroy$)).subscribe((tags) => {
      const dietaryTagCategories = (tags ?? []).filter(tagCategory => tagCategory.isDietary());
      const dietaryTagCategoryIds = dietaryTagCategories.map(category => category.id);
      
      const reservationTags: ReservationTag[] = (JSON.parse(JSON.stringify(this.reservation?.reservationDetails.reservationTags ?? [])))
      .sort((a: ReservationTag, b: ReservationTag) => {
        return this.reservationService.tagByCategoryOrderComperator(a, b);
      });

      this.dietaryTags = reservationTags.filter(tag => dietaryTagCategoryIds.some(id => id === tag.categoryId));
    });
  }

  statusToIcon(status: ReservationStatus) {
    return Common.getReservationStatusIcon(status);
  }

  getCustomerName() {
    return this.reservationService.getCustomerName(this.reservation);
  }

  getStatusIcon(): AppIcon {
    return `ReservationStatus${this.reservation?.reservationDetails.reservationsStatus}` as AppIcon;
  }

  setAssignMode(event: MouseEvent) {

    if (!this.hasAssignTablesPermission()) {
      return;
    }

    event.stopPropagation();

    if (this.reservation?.reservationDetails.allocatedTables != null && this.reservation?.reservationDetails.allocatedTables.trim().length > 0) {
      const tableNames = this.reservation?.reservationDetails.allocatedTables.split(',');
      const tableName = tableNames[0];
      const table = this.tables.find(t => t.tableName === tableName);

      if (table != null && table.sectionId !== this.selectedSection?.sectionId) {
        const section = (this.sections ?? []).find(s => s.sectionId === table.sectionId);

        if (section != null) {
          this.store.dispatch({ type: TablesPlanActions.setSection.type, section });
        }
      }
    }

    this.store.dispatch({ type: TablesPlanActions.setReservationAssignTablesMode.type, assignMode: true, reservation: this.reservation });
    this.store.dispatch({ type: ReservationListActions.setEditingReservationId.type, reservationId: this.reservation?.reservationDetails.reservationId });
  }

  async navigateToReservation() {

    if (this.reservationAssignTablesMode) {
      return;
    }

    // const date = moment(this.reservation?.reservationDetails.arrivalTime);

    // const arrivalTime = moment(this.reservation?.reservationDetails.arrivalTime).toDate();
    // const isCurrentTimeNextDayMorning = Common.timeIsNextDayMorning(arrivalTime);

    // if (isCurrentTimeNextDayMorning) {
    //   date.add(-1, 'days');
    // }

    // set date
    // this.store.dispatch({ type: ReservationListActions.setDate.type, date: date.format('yyyy-MM-DD') });

    this.router.navigate([`/reservations/edit/${this.reservation?.reservationDetails.reservationId ?? this.reservation?.reservationDetails.tmpId}`]);
  }

  onShowChangeStatusModal(event: MouseEvent) {
    event.stopPropagation();

    this.store.dispatch({ type: ReservationActions.setQuickChangeStatusReservation.type, reservation: this.reservation });
  }

  deselctTables() {
    this.store.dispatch({ type: TablesPlanActions.setSelectedTables.type, tables: [] });
  }

  hasAssignTablesPermission() {
    return this.utilsService.canWrite('AssignTable');
  }
  
  hasReadSourcePermission() {
    return this.utilsService.canRead('Source');
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

}
